import { useQueryClient } from 'react-query';

import { ApiError, ApiResponse, useApiMutation, useApiQuery } from './useApi';
import { invalidateCrmQueries } from './useCrm';
import {
    ForecastDay, MetricsPeriod, ProspectingCampaignMetrics, ProspectingCounts, ProspectingHotelActionResult, ProspectingHotelRow, ProspectingHotelsFilters, ProspectingHotelThread,
    ProspectingOverview, ProspectingSendRow, ProspectingSendsFilters, ProspectingSendTemplateOption,
} from '../types/prospectingTracking';

const BASE = '/crm/prospecting';

/** Sans les filtres vides : `?q=` vide ou `state` absent ne partent pas, la clé de cache reste stable. */
function cleanParams<T extends object>(filters: T): Partial<T> {
    return Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== null && value !== '')) as Partial<T>;
}

function useInvalidate() {
    const queryClient = useQueryClient();
    return () => invalidateCrmQueries(queryClient);
}

// ── Lecture ──────────────────────────────────────────────────────────

/** Écran Aujourd'hui : relu chaque minute, seulement quand l'onglet du navigateur est visible. */
export function useProspectingOverview() {
    return useApiQuery<{ data: ProspectingOverview }>(`${BASE}/overview`, {}, { refetchInterval: 60_000, refetchIntervalInBackground: false });
}

/** Statistiques simples d'une campagne sur la période choisie. */
export function useProspectingCampaignMetrics(campaignId: number, periode: MetricsPeriod) {
    return useApiQuery<{ data: ProspectingCampaignMetrics }>(campaignId > 0 ? `${BASE}/campaigns/${campaignId}/metrics` : '', { params: { periode } }, { keepPreviousData: true });
}

export function useProspectingSends(filters: ProspectingSendsFilters) {
    return useApiQuery<ApiResponse<ProspectingSendRow[]>>(`${BASE}/sends`, { params: cleanParams(filters) }, { keepPreviousData: true });
}

/** Trames proposées par le filtre du journal : une par lignée, limitées à la campagne choisie. */
export function useProspectingSendTemplates(campaignId?: number) {
    return useApiQuery<{ data: ProspectingSendTemplateOption[] }>(`${BASE}/sends/templates`, { params: campaignId ? { campaign_id: campaignId } : {} });
}

export function useProspectingForecast(days = 7) {
    return useApiQuery<{ data: ForecastDay[] }>(`${BASE}/forecast`, { params: { days } });
}

export function useProspectingHotels(filters: ProspectingHotelsFilters) {
    return useApiQuery<ApiResponse<ProspectingHotelRow[]>>(`${BASE}/hotels`, { params: cleanParams(filters) }, { keepPreviousData: true });
}

export function useProspectingHotel(id: number) {
    return useApiQuery<{ data: ProspectingHotelThread }>(id > 0 ? `${BASE}/hotels/${id}` : '');
}

/** Pastilles des onglets et du menu ; `interval` false : pas de sondage. */
export function useProspectingCounts(enabled: boolean, interval: number | false) {
    return useApiQuery<{ data: ProspectingCounts }>(`${BASE}/counts`, {}, { enabled, refetchInterval: interval, refetchIntervalInBackground: false });
}

// ── Actions sur un hôtel ─────────────────────────────────────────────

type HotelActionResponse = { data: ProspectingHotelActionResult };

export function useStopHotel() {
    const invalidate = useInvalidate();
    return useApiMutation<HotelActionResponse, ApiError, { hotelId: number }>('post', (v) => `${BASE}/hotels/${v.hotelId}/stop`, { onSuccess: invalidate });
}

export function usePauseHotel() {
    const invalidate = useInvalidate();
    return useApiMutation<HotelActionResponse, ApiError, { hotelId: number; reason?: string | null }>(
        'post', (v) => `${BASE}/hotels/${v.hotelId}/pause`, { onSuccess: invalidate },
    );
}

export function useResumeHotel() {
    const invalidate = useInvalidate();
    return useApiMutation<HotelActionResponse, ApiError, { hotelId: number }>('post', (v) => `${BASE}/hotels/${v.hotelId}/resume`, { onSuccess: invalidate });
}

export function useSendNow() {
    const invalidate = useInvalidate();
    return useApiMutation<HotelActionResponse, ApiError, { targetId: number }>('post', (v) => `${BASE}/targets/${v.targetId}/send-now`, { onSuccess: invalidate });
}

export function useSkipStep() {
    const invalidate = useInvalidate();
    return useApiMutation<HotelActionResponse, ApiError, { targetId: number }>('post', (v) => `${BASE}/targets/${v.targetId}/skip-step`, { onSuccess: invalidate });
}
